import { Box, CircularProgress, Typography } from '@mui/material';
import { CircleAlert, Inbox, Lock, RefreshCw, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import type { ColorTokens } from '../tokens';
import { Button } from './Button';
import { Icon } from './Icon';

/**
 * Blocs d'etat reutilisables : chargement, vide, erreur, acces refuse.
 *
 * Chaque ecran du back-office passe par ces quatre etats, et l'exploitant doit
 * reconnaitre **la meme situation sous la meme forme** d'un ecran a l'autre —
 * sans quoi un 403 sur la fiche et un 403 dans la liste se liraient comme deux
 * pannes differentes. Ils remplacent les variantes ecrites par les ecrans de
 * comptes (`Loading`, `ErrorPanel`, le panneau « acces » d'`AccountsUi`).
 *
 * Trois regles :
 * - **le vide n'est pas une erreur** : il dit ce qui manque, et que c'est normal ;
 * - **une erreur propose toujours « Réessayer »** et affiche le message deja
 *   traduit par le client HTTP ;
 * - **un acces refuse (403) ne deconnecte jamais** : la personne est bien
 *   authentifiee, il lui manque un role, et le bloc dit comment il s'obtient.
 */

interface StateFrameProps {
  readonly icon: LucideIcon;
  readonly ink: keyof ColorTokens;
  readonly fill: keyof ColorTokens;
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
  readonly role?: 'alert';
}

const StateFrame = ({ icon, ink, fill, title, children, action, role }: StateFrameProps): ReactElement => (
  <Box
    role={role}
    sx={{
      display: 'flex',
      gap: 2,
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      p: 2.5,
      borderRadius: '16px',
      border: 1,
      borderColor: 'tokens.border',
      bgcolor: 'tokens.surface',
    }}
  >
    <Box sx={{ display: 'inline-flex', p: 1.25, borderRadius: '50%', bgcolor: `tokens.${fill}`, color: `tokens.${ink}` }}>
      <Icon icon={icon} size={20} />
    </Box>
    <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
      <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, lineHeight: '22px' }}>
        {title}
      </Typography>
      {children === undefined ? null : (
        <Typography component="div" sx={{ mt: 0.5, fontSize: 14, lineHeight: '20px', color: 'tokens.textMuted' }}>
          {children}
        </Typography>
      )}
    </Box>
    {action === undefined ? null : <Box sx={{ alignSelf: 'center' }}>{action}</Box>}
  </Box>
);

/** Attente, annoncee une fois. On ne dessine pas un tableau vide en attendant. */
export const LoadingState = ({ label }: { readonly label: string }): ReactElement => (
  <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
    <CircularProgress size={20} aria-hidden />
    <Typography sx={{ fontSize: 14 }}>{label}</Typography>
  </Box>
);

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: ReactNode;
  readonly icon?: LucideIcon;
  readonly action?: ReactNode;
}

export const EmptyState = ({ title, description, icon = Inbox, action }: EmptyStateProps): ReactElement => (
  <StateFrame icon={icon} ink="textMuted" fill="neutralSoft" title={title} action={action}>
    {description}
  </StateFrame>
);

export interface ErrorStateProps {
  /** Message deja traduit (le client HTTP traduit les codes du serveur). */
  readonly message: string;
  readonly onRetry: () => void;
  readonly title?: string;
  /**
   * Libelle du bouton. « Réessayer » par defaut ; une erreur que relancer ne
   * corrige pas (des filtres refuses, 422) propose ce qui la corrige.
   */
  readonly retryLabel?: string;
}

export const ErrorState = ({
  message,
  onRetry,
  title = 'Le chargement a échoué',
  retryLabel = 'Réessayer',
}: ErrorStateProps): ReactElement => (
  <StateFrame
    role="alert"
    icon={CircleAlert}
    ink="criticalText"
    fill="criticalSoft"
    title={title}
    action={
      <Button icon={RefreshCw} onClick={onRetry}>
        {retryLabel}
      </Button>
    }
  >
    {message}
  </StateFrame>
);

export interface AccessDeniedProps {
  readonly title?: string;
  /** Ce qui manque et comment l'obtenir. */
  readonly children?: ReactNode;
}

export const AccessDenied = ({
  title = 'Accès refusé',
  children = 'Votre compte est bien connecté, mais son rôle ne permet pas d’ouvrir cet écran.',
}: AccessDeniedProps): ReactElement => (
  <StateFrame icon={Lock} ink="infoText" fill="infoSoft" title={title}>
    {children}
  </StateFrame>
);
